/**
 * Tavern-phase tribe payoffs.
 *
 * Two of the six verbs do their work between rounds rather than in combat,
 * because that is where their fantasy lives:
 *
 * - PLACA (Plaqueros) — you tag the same carry every round and watch the
 *   number climb. Tags are permanent AND counted, so the Plaqueros 6
 *   cash-in (synergyAuras) reads the counter and pays out per tag. Stacks
 *   grow linearly per round while the payout scales off the stack count,
 *   which is what bends the curve upward late.
 *
 * - RAZA (La Clica) — the block refills every round. Each end of turn adds
 *   a Morrito and pumps every token already standing, so the swarm you
 *   bring to round 10 is nothing like the one from round 4.
 *
 * Combat-phase verbs (CODAZO, SETLIST, SEGUNDO AIRE, BRINCO) live in
 * synergyAuras.ts / combatEngine.ts instead.
 */

import type { Card } from './data/cards/types';
import {
  TRIBE_DEFINITIONS,
  TribeSynergyBreakpoint,
  TribeDefinition,
  getActiveBreakpoint,
} from './data/tribes';
import { makeMorrito } from './data/cards/tokens';
import { applyPlaqueTag } from './plaque';

export interface TribeEndOfTurnResult {
  board: (Card | null)[];
  /** Human-readable log lines, one per verb that fired. Drives tavern FX. */
  log: string[];
}

/**
 * Applies every tavern-phase tribe payoff to a board.
 *
 * Pure: returns a new board array with new card objects for anything it
 * touched, so the match store can drop the result straight into set().
 *
 * @param board The player's 7-slot board (nulls are empty slots)
 * @param newInstanceId Factory for token instance ids — injected so callers
 *   can keep summons deterministic under a seeded run.
 */
export function applyTribeEndOfTurn(
  board: (Card | null)[],
  newInstanceId: () => string = () => crypto.randomUUID(),
): TribeEndOfTurnResult {
  const next: (Card | null)[] = [...board];
  const log: string[] = [];

  for (const tribe of TRIBE_DEFINITIONS) {
    const count = next.filter(c => c && c.tribe === tribe.id).length;
    if (count < 2) continue;

    const bp = getActiveBreakpoint(tribe, count);
    if (!bp) continue;

    // --- PLACA: tag the carry, then the rest of the crew ---
    if (bp.plaqueTag) {
      const members = next
        .map((card, index) => ({ card, index }))
        .filter((item): item is { card: Card; index: number } =>
          !!item.card && item.card.tribe === tribe.id,
        );

      if (members.length > 0) {
        // The carry is whoever already hits hardest — keep feeding the leader
        // so the snowball concentrates instead of spreading thin.
        const carry = members.reduce(
          (best, item) => (item.card.attack > best.card.attack ? item : best),
          members[0],
        );

        const tagged = { ...carry.card };
        applyPlaqueTag(tagged, bp.plaqueTag.count);
        next[carry.index] = tagged;

        const othersCount = bp.plaqueTag.othersCount ?? 0;
        if (othersCount > 0) {
          for (const item of members) {
            if (item.index === carry.index) continue;
            const other = { ...item.card };
            applyPlaqueTag(other, othersCount);
            next[item.index] = other;
          }
        }

        log.push(
          `${tribe.verb}: ${tagged.name} tagged ${bp.plaqueTag.count}× ` +
          `(${tagged.plaqueStacks} total)`,
        );
      }
    }

    // --- RAZA: pump the swarm, then add another body ---
    // Buff first so the newly-summoned Morrito arrives at its printed stats
    // rather than double-dipping the same end-of-turn buff.
    if (bp.tokenBuff) {
      let buffed = 0;
      for (let i = 0; i < next.length; i++) {
        const unit = next[i];
        if (!unit || !unit.tags?.includes('token')) continue;
        next[i] = {
          ...unit,
          attack: unit.attack + bp.tokenBuff.attack,
          health: unit.health + bp.tokenBuff.health,
        };
        buffed++;
      }
      if (buffed > 0) {
        log.push(
          `${tribe.verb}: ${buffed} token${buffed === 1 ? '' : 's'} ` +
          `+${bp.tokenBuff.attack}/+${bp.tokenBuff.health}`,
        );
      }
    }

    if (bp.summonToken) {
      const free = next.findIndex(slot => slot === null);
      if (free >= 0) {
        const morrito = makeMorrito(
          bp.summonToken.attack,
          bp.summonToken.health,
          `${tribe.id}_morrito_${newInstanceId()}`,
        );
        next[free] = morrito;
        log.push(
          `${tribe.verb}: summoned a ${bp.summonToken.attack}/${bp.summonToken.health} Morrito`,
        );
      }
    }
  }

  return { board: next, log };
}
